(() => {
  'use strict';

  if (window.__lldgAnnouncementLoaded) {
    document.dispatchEvent(new CustomEvent('lldg:announcement:refresh'));
    return;
  }
  window.__lldgAnnouncementLoaded = true;

  const ROOT_SELECTOR = '[data-lldg-announcement]';
  const ITEM_SELECTOR = '[data-announcement-item]';
  const instances = new Map();
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  const listenToMedia = (query, callback, add) => {
    const modernMethod = add ? 'addEventListener' : 'removeEventListener';
    if (typeof query[modernMethod] === 'function') {
      query[modernMethod]('change', callback);
    } else {
      query[add ? 'addListener' : 'removeListener'](callback);
    }
  };

  class LldgAnnouncement {
    constructor(root) {
      this.root = root;
      this.items = Array.from(root.querySelectorAll(ITEM_SELECTOR));
      this.track = root.querySelector('[data-announcement-items]');
      this.toggle = root.querySelector('[data-announcement-action="toggle"]');
      this.mobileQuery = window.matchMedia('(max-width: 767px)');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.interval = Math.max(3000, Number(root.dataset.mobileInterval) || 5000);
      this.activeIndex = Math.max(
        0,
        this.items.findIndex((item) => item.classList.contains('is-active')),
      );
      this.manuallyPaused = designMode || root.dataset.mobileAutoplay !== 'true';
      this.isVisible = true;
      this.linkHasFocus = false;
      this.timer = 0;
      this.transitionTimer = 0;
      this.focusFrame = 0;

      this.handleClick = this.handleClick.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.handleVisibility = this.handleVisibility.bind(this);

      root.addEventListener('click', this.handleClick);
      root.addEventListener('focusin', this.handleFocusIn);
      root.addEventListener('focusout', this.handleFocusOut);
      document.addEventListener('visibilitychange', this.handleVisibility);
      listenToMedia(this.mobileQuery, this.handleMediaChange, true);
      listenToMedia(this.motionQuery, this.handleMediaChange, true);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            this.isVisible = entries.some((entry) => entry.isIntersecting);
            this.syncTimer();
          },
          { threshold: 0.05 },
        );
        this.observer.observe(root);
      }

      this.update(false);
      this.syncTimer();
    }

    normalizedIndex(index) {
      const total = this.items.length;
      return total ? (Number(index) + total) % total : 0;
    }

    canAnimate() {
      return !this.motionQuery.matches &&
        document.documentElement.dataset.motionEnabled !== 'false';
    }

    playbackUnavailableReason() {
      if (!this.mobileQuery.matches) return 'Mobile announcement rotation is available on smaller screens';
      if (document.documentElement.dataset.motionEnabled === 'false') {
        return 'Announcement rotation is disabled in theme settings';
      }
      if (this.motionQuery.matches) {
        return 'Announcement rotation is disabled by reduced motion preference';
      }
      if (this.items.length < 2) return 'Announcement rotation requires multiple messages';
      return '';
    }

    updatePlaybackControl() {
      if (!this.toggle) return;
      const unavailableReason = this.playbackUnavailableReason();
      const paused = Boolean(unavailableReason) || this.manuallyPaused;
      const label = unavailableReason ||
        (paused ? 'Play announcement rotation' : 'Pause announcement rotation');

      this.toggle.disabled = Boolean(unavailableReason);
      this.toggle.setAttribute('aria-label', label);
      this.toggle.setAttribute('title', label);
      this.toggle.setAttribute('aria-pressed', String(paused));
      if (this.track) this.track.setAttribute('aria-live', paused ? 'polite' : 'off');

      const icon = this.toggle.querySelector('.playback-icon');
      if (icon) {
        icon.classList.toggle('is-play', paused);
        icon.classList.toggle('is-pause', !paused);
      }
    }

    update(resetTransition = true) {
      if (resetTransition) {
        window.clearTimeout(this.transitionTimer);
        this.items.forEach((item) => item.classList.remove('is-leaving'));
      }

      this.items.forEach((item, index) => {
        const active = index === this.activeIndex;
        item.classList.toggle('is-active', active);
        item.setAttribute(
          'aria-hidden',
          String(this.mobileQuery.matches ? !active : false),
        );
      });
      this.updatePlaybackControl();
    }

    show(index, direction = 1, restart = true) {
      const nextIndex = this.normalizedIndex(index);
      if (nextIndex === this.activeIndex || !this.items.length) {
        if (restart) this.syncTimer();
        return;
      }

      const current = this.items[this.activeIndex];
      const next = this.items[nextIndex];
      const animate = this.mobileQuery.matches && this.canAnimate();

      window.clearTimeout(this.transitionTimer);
      this.items.forEach((item) => item.classList.remove('is-leaving'));
      this.root.classList.toggle('is-reverse', direction < 0);

      if (animate) {
        next.classList.remove('is-active');
        void this.root.offsetWidth;
        current.classList.remove('is-active');
        current.classList.add('is-leaving');
        next.classList.add('is-active');
        this.transitionTimer = window.setTimeout(() => {
          current.classList.remove('is-leaving');
        }, 560);
      } else {
        current.classList.remove('is-active');
        next.classList.add('is-active');
      }

      this.activeIndex = nextIndex;
      this.update(false);
      if (restart) this.syncTimer();
    }

    setPaused(paused) {
      this.manuallyPaused = Boolean(paused);
      this.updatePlaybackControl();
      this.syncTimer();
    }

    handleClick(event) {
      const control = event.target.closest('[data-announcement-action]');
      if (!control || !this.root.contains(control)) return;

      event.preventDefault();
      const action = control.dataset.announcementAction;
      if (action === 'previous') {
        this.setPaused(true);
        this.show(this.activeIndex - 1, -1);
      } else if (action === 'next') {
        this.setPaused(true);
        this.show(this.activeIndex + 1, 1);
      } else if (action === 'toggle' && !this.playbackUnavailableReason()) {
        this.setPaused(!this.manuallyPaused);
      }
    }

    handleFocusIn(event) {
      if (!(event.target instanceof Element)) return;
      if (!event.target.closest('.service-note__item a')) return;
      this.linkHasFocus = true;
      this.syncTimer();
    }

    handleFocusOut() {
      window.cancelAnimationFrame(this.focusFrame);
      this.focusFrame = window.requestAnimationFrame(() => {
        this.linkHasFocus = Boolean(
          this.root.querySelector('.service-note__item a:focus'),
        );
        this.syncTimer();
      });
    }

    handleMediaChange() {
      if (!this.mobileQuery.matches) this.activeIndex = 0;
      this.update();
      this.syncTimer();
    }

    handleVisibility() {
      this.syncTimer();
    }

    syncTimer() {
      window.clearTimeout(this.timer);
      this.timer = 0;
      this.updatePlaybackControl();

      if (
        this.playbackUnavailableReason() ||
        this.manuallyPaused ||
        this.linkHasFocus ||
        document.hidden ||
        !this.isVisible
      ) {
        return;
      }

      this.timer = window.setTimeout(() => {
        this.show(this.activeIndex + 1, 1, false);
        this.syncTimer();
      }, this.interval);
    }

    selectEditorBlock(target) {
      const item = target.matches(ITEM_SELECTOR) ? target : target.closest(ITEM_SELECTOR);
      if (!item || !this.root.contains(item)) return;
      this.setPaused(true);
      this.show(this.items.indexOf(item), 1, false);
    }

    destroy() {
      window.clearTimeout(this.timer);
      window.clearTimeout(this.transitionTimer);
      window.cancelAnimationFrame(this.focusFrame);
      this.root.removeEventListener('click', this.handleClick);
      this.root.removeEventListener('focusin', this.handleFocusIn);
      this.root.removeEventListener('focusout', this.handleFocusOut);
      document.removeEventListener('visibilitychange', this.handleVisibility);
      listenToMedia(this.mobileQuery, this.handleMediaChange, false);
      listenToMedia(this.motionQuery, this.handleMediaChange, false);
      if (this.observer) this.observer.disconnect();
    }
  }

  const rootsWithin = (scope) => {
    const roots = [];
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    if (scope && typeof scope.querySelectorAll === 'function') {
      roots.push(...scope.querySelectorAll(ROOT_SELECTOR));
    }
    return Array.from(new Set(roots));
  };

  const initialize = (scope = document) => {
    rootsWithin(scope).forEach((root) => {
      const current = instances.get(root);
      if (current) current.destroy();
      instances.set(root, new LldgAnnouncement(root));
    });
  };

  const destroyWithin = (scope) => {
    instances.forEach((instance, root) => {
      if (root === scope || (scope instanceof Element && scope.contains(root))) {
        instance.destroy();
        instances.delete(root);
      }
    });
  };

  const ready = () => initialize(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready, { once: true });
  } else {
    ready();
  }

  document.addEventListener('lldg:announcement:refresh', () => initialize(document));
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    instances.forEach((instance) => instance.selectEditorBlock(event.target));
  });
})();
